import { StandardEvents } from '@shopify/events';

const openers = new WeakMap();
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const closingAnimations = new WeakMap();
function closeDialog(dialog, immediate = false) {
  if (!dialog?.open || closingAnimations.has(dialog)) return;
  if (immediate || reducedMotion.matches || !dialog.animate) {
    dialog.close();
    return;
  }
  dialog.dataset.closing = 'true';
  const frames = dialog.classList.contains('sp-menu')
    ? [
        { opacity: 1, transform: 'none' },
        { opacity: 0, transform: 'translateX(12px)' },
      ]
    : dialog.classList.contains('sp-search')
      ? [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: 'translateY(-8px)' },
        ]
      : [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: 'translateY(6px)' },
        ];
  // Motion tokens from sp-sport.css. Shopify's CDN minifies the stylesheet (250ms is served as .25s), so read the unit.
  const root = getComputedStyle(document.documentElement);
  const exitValue = root.getPropertyValue('--sp-exit').trim();
  const exitNumber = parseFloat(exitValue);
  const exitMs = Number.isNaN(exitNumber) ? 250 : exitValue.endsWith('ms') ? exitNumber : exitNumber * 1000;
  const animation = dialog.animate(frames, {
    duration: exitMs,
    easing: root.getPropertyValue('--sp-ease').trim() || 'ease-out',
  });
  closingAnimations.set(dialog, animation);
  animation.finished
    .catch(() => {})
    .finally(() => {
      closingAnimations.delete(dialog);
      delete dialog.dataset.closing;
      if (dialog.open) dialog.close();
    });
}
function initializeDialogs(root = document) {
  root.querySelectorAll('.sp-dialog:not([data-ready])').forEach((dialog) => {
    dialog.dataset.ready = 'true';
    dialog.addEventListener('close', () => {
      const opener = openers.get(dialog);
      opener?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('dialog[open]')) opener?.focus();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog(dialog);
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const nodes = [
        ...dialog.querySelectorAll(
          'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled]),summary',
        ),
      ].filter((n) => n.getClientRects().length);
      const first = nodes[0],
        last = nodes.at(-1);
      if (!first) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  });
}
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-sp-open]');
  if (opener && opener.tagName === 'A') event.preventDefault();
  if (opener) {
    const dialog = document.getElementById(opener.dataset.spOpen);
    if (dialog && !dialog.open) {
      document.querySelectorAll('.sp-dialog[open]').forEach((other) => closeDialog(other, true));
      openers.set(dialog, opener);
      opener.setAttribute('aria-expanded', 'true');
      dialog.showModal();
      if (dialog.id === 'sp-search') dialog.querySelector('input[type="search"]')?.focus();
    }
    return;
  }
  const dialog = event.target.closest('.sp-dialog');
  if (!dialog) return;
  if (event.target.closest('a[href]')) closeDialog(dialog);
  const rect = dialog.getBoundingClientRect();
  if (
    event.target.closest('[data-sp-close]') ||
    (event.target === dialog &&
      (event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom))
  )
    closeDialog(dialog);
});
// Product grid category, search and sort moved to sp-collection.js (loaded only on collection and search pages).
initializeDialogs();
document.addEventListener('shopify:section:load', (event) => initializeDialogs(event.target));

// Accessible field messages for the contact form and the email sign-up ([data-sp-validate]). A form with novalidate
// (the sign-up) is checked here on submit instead of by the browser's pop-up: the message shows under the field
// (after its [data-sp-field] wrapper, if any), in the form's own words (data-sp-error) when it has them, and focus
// moves to the first field to fix.
function initializeContact(root = document) {
  root
    .querySelectorAll('.sp-contact-form:not([data-sp-validation]),[data-sp-validate]:not([data-sp-validation])')
    .forEach((form) => {
      form.dataset.spValidation = 'true';
      const fields = [...form.querySelectorAll('input[required],select[required],textarea[required]')];
      function feedback(field) {
        let hint = form.querySelector(`#${field.id}-error`);
        if (!hint) {
          hint = document.createElement('span');
          hint.id = `${field.id}-error`;
          hint.className = 'sp-field-error';
          (field.closest('[data-sp-field]') || field).after(hint);
          field.setAttribute('aria-describedby', `${field.getAttribute('aria-describedby') || ''} ${hint.id}`.trim());
        }
        hint.textContent = field.validity.valid ? '' : field.dataset.spError || field.validationMessage;
        if (field.validity.valid) field.removeAttribute('aria-invalid');
        else field.setAttribute('aria-invalid', 'true');
      }
      fields.forEach((field) => {
        field.addEventListener('invalid', () => feedback(field));
        field.addEventListener('input', () => {
          if (field.hasAttribute('aria-invalid')) feedback(field);
        });
        field.addEventListener('change', () => {
          if (field.hasAttribute('aria-invalid')) feedback(field);
        });
      });
      if (form.noValidate)
        form.addEventListener('submit', (event) => {
          if (form.checkValidity()) return;
          event.preventDefault();
          form.querySelector(':invalid')?.focus();
        });
      // Round 15: Shopify's answer to a sign-up is printed by every sign-up form on the page; the address after #
      // names the one the shopper used. The others drop the error (their field is fine) and leave focus alone.
      const used = location.hash.slice(1);
      if (form.matches('[data-sp-signup]') && used && form.id !== used) {
        form.querySelector('.sp-form-errors')?.remove();
        form.querySelectorAll('[aria-invalid]').forEach((field) => {
          field.removeAttribute('aria-invalid');
          field.removeAttribute('aria-describedby');
        });
        return;
      }
      form.querySelector('.sp-form-success,.sp-form-errors')?.focus({ preventScroll: true });
    });
}
initializeContact();
document.addEventListener('shopify:section:load', (event) => initializeContact(event.target));
const purchaseTimers = new WeakMap();
// Round 26: "Adding…" and "Added" come from the store's wording (snippets/sp-script-wording.liquid); today's words
// if an entry is missing.
const purchaseWord = (key, fallback) => {
  try {
    const text = JSON.parse(document.getElementById('sp-wording')?.textContent || '{}')[key];
    if (!text) return fallback;
    const decoder = document.createElement('textarea');
    decoder.innerHTML = text;
    return decoder.value;
  } catch {
    return fallback;
  }
};
document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  if (event.action !== 'add') return;
  const form = event.target.closest?.('product-form-component');
  const section = form?.closest('.shopify-section');
  const buttons = section
    ? [...section.querySelectorAll('product-form-component [ref="addToCartButton"],.sticky-add-to-cart__button')]
    : [];
  const clear = (button) => {
    clearTimeout(purchaseTimers.get(button));
    purchaseTimers.delete(button);
    delete button.dataset.spPurchase;
    button.removeAttribute('aria-busy');
    button.querySelector('.sp-purchase-state')?.remove();
  };
  buttons.forEach((button) => {
    clear(button);
    button.dataset.spPurchase = 'pending';
    button.setAttribute('aria-busy', 'true');
    const label = document.createElement('span');
    label.className = 'sp-purchase-state';
    label.setAttribute('aria-hidden', 'true');
    label.textContent = purchaseWord('adding', 'Adding…');
    button.append(label);
  });
  try {
    const result = await event.promise;
    if (result?.detail?.didError) {
      buttons.forEach(clear);
      return;
    }
    buttons.forEach((button) => {
      button.removeAttribute('aria-busy');
      button.dataset.spPurchase = 'added';
      const label = button.querySelector('.sp-purchase-state');
      if (label)
        label.innerHTML =
          '<span class="sp-added"><svg class="sp-tick" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3.1L13 4.6"/></svg></span>';
      label?.querySelector('.sp-added')?.append(purchaseWord('added', 'Added'));
      purchaseTimers.set(
        button,
        setTimeout(() => {
          if (button.dataset.spPurchase === 'added') clear(button);
        }, 1200),
      );
    });
    if (reducedMotion.matches) return;
    const badge = document.querySelector('.sp-header .cart-bubble');
    badge?.animate(
      [
        { opacity: 0.65, transform: 'scale(.96)' },
        { opacity: 1, transform: 'scale(1.06)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 180, easing: 'ease-out' },
    );
  } catch {
    buttons.forEach(clear); /* Shopify's form owns error messages. */
  }
});
document.addEventListener(
  'click',
  (event) => {
    if (event.target.closest('[data-sp-purchase="pending"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);
document.addEventListener(
  'keydown',
  (event) => {
    if (['Enter', ' '].includes(event.key) && event.target.closest('[data-sp-purchase="pending"]')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true,
);
